import { db } from '../../db/indexeddb'
import {
  getCloudSections,
  getCloudSectionIds,
  getCloudSectionsByIds,
  getDeletedCloudSections,
  createCloudSection,
  updateCloudSection,
  deleteCloudSection,
} from '../../supabase/queries'
import { CloudSectionSchema, CloudSectionPayloadSchema } from '../sync-schemas'
import { resolveCloudProjectId, resolveLocalProjectId } from '../sync-resolve'
import type { PullDescriptor } from '../sync-pull'
import type { PushDescriptor } from '../sync-push'
import type { Section } from '../../db/models'

/* ── Section ─────────────────────────────────────────── */

export const sectionPullDescriptor: PullDescriptor<any> = {
  entityName: 'section',
  settingsKey: 'lastSectionSyncTime',

  fetchRemote: (userId, since) => getCloudSections(userId, since),
  fetchRemoteIds: (userId) => getCloudSectionIds(userId),
  fetchRemoteByIds: (userId, ids) => getCloudSectionsByIds(userId, ids),
  fetchDeletedRemote: (userId, since) => getDeletedCloudSections(userId, since),

  safeParse: (raw) => CloudSectionSchema.safeParse(raw),
  getRemoteId: (r) => r.id,
  getRemoteTimestamp: (r) => r.updated_at,
  getRemoteLocalId: (r) => r.local_id,

  table: db.sections,
  getAllLocal: () => db.sections.toArray(),

  shouldForceFullSync: async () => {
    const count = await db.sections.filter(s => !s.deletedAt).count()
    return count === 0
  },

  buildLocalInsert: async (remote, userId) => {
    const localProjectId = await resolveLocalProjectId(remote.project_id)
    if (!localProjectId) return null // project not yet synced locally — skip silently
    return {
      id: crypto.randomUUID(),
      title: remote.title,
      projectId: localProjectId,
      userId,
      cloudId: remote.id,
      order: remote.order,
      collapsed: remote.collapsed,
      importedFrom: remote.imported_from ?? null,
      description: remote.description ?? null,
      archivedAt: remote.archived_at ? new Date(remote.archived_at) : null,
      createdAt: new Date(remote.created_at),
      updatedAt: new Date(remote.updated_at),
      lastModified: new Date(remote.updated_at),
      synced: true,
      syncedAt: new Date(),
      deletedAt: null,
    }
  },

  buildLocalUpdate: async (remote) => ({
    title: remote.title,
    order: remote.order,
    collapsed: remote.collapsed,
    ...('imported_from' in remote ? { importedFrom: remote.imported_from ?? null } : {}),
    // Fehlt die Spalte (Cloud ohne Migration), bleibt der lokale Wert.
    ...('description' in remote ? { description: remote.description ?? null } : {}),
    ...('archived_at' in remote ? { archivedAt: remote.archived_at ? new Date(remote.archived_at) : null } : {}),
    updatedAt: new Date(remote.updated_at),
  }),
}

export const sectionPushDescriptor: PushDescriptor<Section> = {
  entityName: 'CloudSection',
  getLocal: (id) => db.getSection(id),
  getLocalCloudId: (s) => s.cloudId,
  getLocalId: (s) => s.id,
  table: db.sections,

  canCreate: async (section) => {
    if (section.projectId) {
      const cloudProjectId = await resolveCloudProjectId(section.projectId)
      if (!cloudProjectId) return { defer: true }
    }
    return { defer: false }
  },

  canUpdate: async (section) => {
    if (section.projectId) {
      const cloudProjectId = await resolveCloudProjectId(section.projectId)
      if (!cloudProjectId) return { defer: true }
    }
    return { defer: false }
  },

  buildCreatePayload: async (section) => {
    const cloudProjectId = await resolveCloudProjectId(section.projectId)
    const payload: Record<string, any> = CloudSectionPayloadSchema.parse({
      project_id: cloudProjectId,
      title: section.title,
      order: section.order,
      collapsed: section.collapsed,
      local_id: section.id,
      imported_from: section.importedFrom ?? null,
      description: section.description ?? null,
      archived_at: section.archivedAt ? section.archivedAt.toISOString() : null,
    })
    // Ohne Wert nicht mitsenden: Ein neuer Abschnitt kommt so auch in einer Cloud an, der
    // die Spalte noch fehlt.
    if (payload.description == null) delete payload.description
    if (payload.archived_at == null) delete payload.archived_at
    return payload
  },

  buildUpdatePayload: async (section) => {
    const cloudProjectId = await resolveCloudProjectId(section.projectId)
    const payload: Record<string, any> = CloudSectionPayloadSchema.parse({
      project_id: cloudProjectId,
      title: section.title,
      order: section.order,
      collapsed: section.collapsed,
      local_id: section.id,
      imported_from: section.importedFrom ?? null,
      // Immer mit, auch als null: Eine geleerte Beschreibung muss in der Cloud ankommen (Checkliste 17).
      description: section.description ?? null,
      // Immer mit, auch als null: Wiederherstellen muss in der Cloud ankommen (Checkliste 17).
      archived_at: section.archivedAt ? section.archivedAt.toISOString() : null,
    })
    if (!section.deletedAt) payload.deleted_at = null
    return payload
  },

  createCloud: (userId, payload) => createCloudSection(userId, payload),
  updateCloud: (cloudId, payload, userId) => updateCloudSection(cloudId, payload, userId),
  deleteCloud: (cloudId, userId) => deleteCloudSection(cloudId, userId),
}
